/* lang-freq.js — English word-frequency, CEFR level and lemmatisation
 * engine for Timbrica language tools.
 *
 * LangFreq: rank(word), cefrBand(word), lemmatize(word), profile(text).
 * Data: /data/lang/freq/words.json (word→rank) + cefr.json (word→CEFR).
 *
 * Plain IIFE, no dependencies. Exposes window.LangFreq + module.exports.
 */
(function (root, factory) {
  'use strict';
  var L = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = L;
  if (typeof window !== 'undefined') window.LangFreq = L;
  else if (root) root.LangFreq = L;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ── CEFR band by frequency rank (heuristic fallback) ─────────── */
  var BANDS = [['A1', 800], ['A2', 1600], ['B1', 3200], ['B2', 6500], ['C1', 14000]];
  var BANDN = { A1: 1, A2: 2, B1: 3, B2: 4, C1: 5, C2: 6 };
  function bandByRank(rank) {
    if (!rank) return null;
    for (var i = 0; i < BANDS.length; i++) if (rank <= BANDS[i][1]) return BANDS[i][0];
    return 'C2';
  }

  /* ── Lemmatiser ───────────────────────────────────────────────── */
  // Irregular inflected form → base. Verbs, then irregular plurals.
  var IRREGULAR = {
    was: 'be', were: 'be', been: 'be', being: 'be', is: 'be', are: 'be', am: 'be',
    had: 'have', has: 'have', having: 'have', did: 'do', does: 'do', done: 'do', doing: 'do',
    went: 'go', gone: 'go', going: 'go', goes: 'go', made: 'make', making: 'make',
    said: 'say', saying: 'say', says: 'say', came: 'come', coming: 'come',
    took: 'take', taken: 'take', taking: 'take', saw: 'see', seen: 'see', seeing: 'see',
    knew: 'know', known: 'know', knowing: 'know', got: 'get', gotten: 'get', getting: 'get',
    gave: 'give', given: 'give', giving: 'give', found: 'find', finding: 'find',
    thought: 'think', thinking: 'think', told: 'tell', telling: 'tell',
    became: 'become', becoming: 'become', left: 'leave', leaving: 'leave',
    felt: 'feel', feeling: 'feel', brought: 'bring', bringing: 'bring',
    began: 'begin', begun: 'begin', beginning: 'begin', kept: 'keep', keeping: 'keep',
    held: 'hold', holding: 'hold', wrote: 'write', written: 'write', writing: 'write',
    stood: 'stand', standing: 'stand', heard: 'hear', hearing: 'hear',
    let: 'let', letting: 'let', meant: 'mean', meaning: 'mean', set: 'set', setting: 'set',
    met: 'meet', meeting: 'meet', ran: 'run', running: 'run', paid: 'pay', paying: 'pay',
    sat: 'sit', sitting: 'sit', spoke: 'speak', spoken: 'speak', speaking: 'speak',
    lay: 'lie', lain: 'lie', led: 'lead', leading: 'lead', read: 'read', reading: 'read',
    grew: 'grow', grown: 'grow', growing: 'grow', lost: 'lose', losing: 'lose',
    fell: 'fall', fallen: 'fall', falling: 'fall', sent: 'send', sending: 'send',
    built: 'build', building: 'build', understood: 'understand',
    drew: 'draw', drawn: 'draw', drawing: 'draw', broke: 'break', broken: 'break', breaking: 'break',
    spent: 'spend', spending: 'spend', cut: 'cut', cutting: 'cut', rose: 'rise', risen: 'rise',
    drove: 'drive', driven: 'drive', driving: 'drive', bought: 'buy', buying: 'buy',
    wore: 'wear', worn: 'wear', wearing: 'wear', chose: 'choose', chosen: 'choose', choosing: 'choose',
    ate: 'eat', eaten: 'eat', eating: 'eat', threw: 'throw', thrown: 'throw', throwing: 'throw',
    flew: 'fly', flown: 'fly', flying: 'fly', won: 'win', winning: 'win',
    children: 'child', men: 'man', women: 'woman', feet: 'foot', teeth: 'tooth',
    mice: 'mouse', geese: 'goose', people: 'person', lives: 'life', knives: 'knife',
    wives: 'wife', leaves: 'leaf', shelves: 'shelf', wolves: 'wolf', halves: 'half',
    better: 'good', best: 'good', worse: 'bad', worst: 'bad', more: 'much', most: 'much'
  };

  var _words = null;     // word → rank
  var _cefr = null;      // word → CEFR
  var _loadPromise = null;
  var _base = '/data/lang/freq';

  function known(w) { return _words ? Object.prototype.hasOwnProperty.call(_words, w) : null; }

  /* Reduce an inflected word to its dictionary form.
   * The frequency list holds word *forms* (SUBTLEX), so a word being
   * "known" does NOT mean it is a base form — always try to reduce first,
   * and accept a stripped candidate only when it is itself known. */
  function lemmatize(word) {
    var w = String(word || '').toLowerCase().replace(/[^a-z']/g, '');
    if (!w) return '';
    if (IRREGULAR[w]) return IRREGULAR[w];
    if (w.length <= 3) return w;                  // too short to be inflected
    var cands = [];
    var add = function (c) { if (c && c.length >= 3) cands.push(c); };
    if (/ies$/.test(w)) add(w.slice(0, -3) + 'y');
    if (/ied$/.test(w)) add(w.slice(0, -3) + 'y');
    if (/(ier|iest)$/.test(w)) add(w.replace(/(ier|iest)$/, 'y'));
    if (/(sses|shes|ches|xes|zes)$/.test(w)) add(w.slice(0, -2));
    if (/es$/.test(w)) { add(w.slice(0, -1)); add(w.slice(0, -2)); }
    if (/s$/.test(w) && !/(ss|us|is)$/.test(w)) add(w.slice(0, -1));
    if (/ing$/.test(w)) {
      var s = w.slice(0, -3);
      if (s.length >= 3) {
        add(s); add(s + 'e');
        if (s[s.length - 1] === s[s.length - 2]) add(s.slice(0, -1));
      }
    }
    // A four-letter "-eed" word is always a base word, never an inflection —
    // need, seed, feed, deed, weed, heed, reed. Deriving from them produced
    // real dictionary words (need → nee, seed → see, feed → fee, deed → dee)
    // and so passed the known() check silently. There is no counter-example:
    // no four-letter -eed spelling is an inflected form. Longer ones still
    // derive, which is what "agreed" → "agree" needs.
    if (/ed$/.test(w) && !(w.length === 4 && /eed$/.test(w))) {
      var e = w.slice(0, -2);
      if (e.length >= 3) {
        add(e); add(e + 'e');
        if (e[e.length - 1] === e[e.length - 2]) add(e.slice(0, -1));
      }
      add(w.slice(0, -1));                        // -d only: "agreed" → "agree"
    }
    if (/(er|est)$/.test(w)) { add(w.replace(/(er|est)$/, '')); add(w.replace(/(er|est)$/, 'e')); }
    if (/ly$/.test(w)) add(w.slice(0, -2));
    for (var i = 0; i < cands.length; i++) {
      if (known(cands[i])) return cands[i];
    }
    if (known(w)) return w;                       // a base form after all
    return (cands.length && _words === null) ? cands[0] : w;
  }

  /* A contraction is its host word: "don't" is do, "it's" is it, "we'll" is
   * we. SUBTLEX splits clitics off when it counts, so the whole form is in
   * neither list — and a learner's text full of don't / it's / I'm read as
   * C2 (measured 2026-10-29: "I don't think it's going to rain" → C2). The
   * clitic itself (not, is, will, would, have, am, are) is A1. */
  var NEG_HOST = { ca: 'can', wo: 'will', sha: 'shall', ai: 'be' };
  function host(word) {
    var w = String(word || '').toLowerCase().replace(/[’‘`´]/g, "'");
    var m = /^([a-z]+)n't$/.exec(w);
    if (m) return NEG_HOST[m[1]] || m[1];
    m = /^([a-z]+)'(s|re|m|ll|d|ve)$/.exec(w);
    if (m) return m[1];
    if (/^[a-z]+s'$/.test(w)) return w.slice(0, -1);          // students' (plural possessive)
    return w;
  }

  function rank(word) {
    if (!_words) return null;
    var w = host(word);
    return _words[w] || _words[lemmatize(w)] || null;
  }

  function cefrBand(word) {
    return cefrInfo(word).band;
  }

  /* The level of a word and where it came from: 'list' — the CEFR-J /
   * Octanove profile names it (or its base form); 'freq' — only its
   * frequency rank is known, and the level is the rank heuristic; null — the
   * word is in neither (a typo, a name, a very rare word). */
  function cefrInfo(word) {
    var w = host(word);
    var lm = lemmatize(w);
    if (_cefr) {
      // An inflected form the list also names as its own word — "known"
      // (adjective, B2), "working" (B1), "been" (A2) — is still a form of an
      // easier verb: the easier of the two is what a reader needs to know.
      var a = _cefr[w], b = lm !== w ? _cefr[lm] : null;
      if (a && b) return { band: BANDN[a] <= BANDN[b] ? a : b, src: 'list', lemma: lm, rank: rank(w) };
      if (a || b) return { band: a || b, src: 'list', lemma: lm, rank: rank(w) };
    }
    var r = rank(w);
    var b = bandByRank(r);
    return { band: b, src: b ? 'freq' : null, lemma: lm, rank: r };
  }

  /* ── Text profiler ────────────────────────────────────────────── */
  function profile(text) {
    var toks = (String(text || '').toLowerCase().match(/[a-z][a-z']*/g) || []);
    var bands = { A1: 0, A2: 0, B1: 0, B2: 0, C1: 0, C2: 0, unknown: 0 };
    var lemmaSet = {}, typeSet = {}, hard = {};
    var top1k = 0, top2k = 0, top3k = 0;
    for (var i = 0; i < toks.length; i++) {
      var t = toks[i];
      typeSet[t] = 1;
      var lm = lemmatize(t);
      lemmaSet[lm] = 1;
      var r = rank(t);
      if (r) { if (r <= 1000) top1k++; if (r <= 2000) top2k++; if (r <= 3000) top3k++; }
      var b = cefrBand(t);
      if (b && bands[b] !== undefined) bands[b]++; else bands.unknown++;
      if (!b || b === 'C1' || b === 'C2') hard[lm] = 1;
    }
    var n = toks.length || 1;
    var cov = {}, order = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'unknown'], cum = 0, bandOfText = 'C2';
    for (var k = 0; k < order.length; k++) {
      cov[order[k]] = bands[order[k]] / n;
      cum += bands[order[k]];
      if (bandOfText === 'C2' && cum / n >= 0.95 && order[k] !== 'unknown') bandOfText = order[k];
    }
    return {
      tokens: toks.length,
      types: Object.keys(typeSet).length,
      lemmas: Object.keys(lemmaSet).length,
      bands: bands,
      coverage: cov,
      bandOfText: bandOfText,
      hardWords: Object.keys(hard).sort(),
      top1k: top1k / n, top2k: top2k / n, top3k: top3k / n
    };
  }

  function load(opts) {
    if (opts && opts.base) _base = opts.base.replace(/\/$/, '');
    if (_loadPromise) return _loadPromise;
    _loadPromise = Promise.all([
      fetch(_base + '/words.json').then(function (r) { if (!r.ok) throw new Error('words ' + r.status); return r.json(); }),
      fetch(_base + '/cefr.json').then(function (r) { return r.ok ? r.json() : {}; }).catch(function () { return {}; })
    ]).then(function (res) {
      _words = res[0]; _cefr = res[1] || {};
      return true;
    }).catch(function (e) { _loadPromise = null; throw e; });
    return _loadPromise;
  }

  return {
    load: load,
    rank: rank,
    cefrBand: cefrBand,
    cefrInfo: cefrInfo,
    host: host,
    inList: function (w) { return !!(_cefr && _cefr[String(w || '').toLowerCase()]); },
    lemmatize: lemmatize,
    profile: profile,
    bandByRank: bandByRank,
    // Exact membership of a word FORM — no lemmatising. Spelling suggestions
    // (ielts-scorer/analyze.js) test hundreds of candidate strings per word
    // and need the plain lookup; rank() would lemmatise each one.
    known: function (w) { return !!known(String(w || '').toLowerCase()); },
    isLoaded: function () { return !!_words; },
    get ready() { return !!_words; }
  };
});
